import * as React from 'react';
import { Link } from 'react-router-dom';
import {
	GlobalNav,
	GlobalNavItem,
	HorizontalHeader,
	Navigation,
	NavigationItem,
	NavigationSubitem,
	Icon,
	GlobalNavSubitem,
	Switch,
} from '@emorg-prd/standard-react';
import './Header.css';
import { logout, authenticatedUser } from '../authentication/authModule';
import usrImage from '../images/user.png';
import CreateFeedbackModal from './Feedback/components/CreateFeedbackModal';
import { AlertBanner } from '../models/interfaces/AlertBanner';
import AlertModal from './common/modals/AlertModal';
import { UserRole } from '../models/enums/UserRole';
import Urls from '../utils/Urls';

interface HeaderProps {
	currentTheme: string;
	onThemeChange: (event: React.ChangeEvent<any>) => void;
}

interface HeaderState {
	expandedDropdown: boolean;
	createFeedbackModalVisible: boolean;
	alert: AlertBanner;
	isDarkTheme: boolean;
}

export default class Header extends React.Component<HeaderProps, HeaderState> {
	constructor(props) {
		super(props);
		this.state = {
			expandedDropdown: false,
			createFeedbackModalVisible: false,
			alert: {
				visible: false,
			},
			isDarkTheme: false,
		};
	}

	componentDidMount() {
		const savedTheme = localStorage.getItem('theme');
		if (savedTheme === 'dark') {
			this.setState({ isDarkTheme: true });
			document.body.classList.add('em-theme--dark');
		} else {
			this.setState({ isDarkTheme: false });
			document.body.classList.remove('em-theme--dark');
		}

		document.addEventListener('click', this.handleOutsideClick, true)
	}

	
	componentWillUnmount() {
		document.removeEventListener('click', this.handleOutsideClick, true);
	}
	

	handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		this.setState({ isDarkTheme: checked });
		localStorage.setItem('theme', checked ? 'dark' : 'default');
		document.body.classList.toggle('em-theme--dark', checked);
		if (this.props.onThemeChange) {
			this.props.onThemeChange(event);
		}
	};

	externalLink = (href: string, text: string) => {
		return (
			<div className="custom-nav-item">
				<NavigationSubitem url={href} urlTarget="_blank" label={text}/>
				<Icon name="link-external" size="em-c-icon--small" />
			</div>
		);
	};

	handleOutsideClick = (e: MouseEvent) => {
		const headerEl = document.getElementById('portal-header');
		if (!headerEl) return;
	
		const dropdownEl = headerEl.querySelector('.em-js-nav-dropdown.em-is-active') as HTMLElement | null;
		if (!dropdownEl) return;
		
		const target = e.target as Element;

		// 1) Click outside header -> close
		if (!headerEl.contains(target)) {
		this.closeMenu();
		return;
		}

		// 2) Click inside dropdown -> NO close
		const clickedInsideDropdown = target.closest('.em-js-nav-dropdown');
		if (clickedInsideDropdown) {
		return;
		}

		// 3) Click on dropdown father -> close
		const clickedTrigger = target.closest('.em-js-nav-dropdown-trigger');
		if (clickedTrigger) {
			this.closeMenu();
			e.stopPropagation();
			return;
		}

		// 4) Any click inside header, outside dropdown -> close
		this.closeMenu();

	}
	
	
	closeMenu = () => {
		this.setState({ expandedDropdown: false });
	
		const headerEl = document.getElementById('portal-header');
		headerEl?.classList.remove('em-is-active');
	    headerEl?.querySelector('.em-js-nav-dropdown.em-is-active')?.classList.remove('em-is-active');
	    headerEl?.querySelector('.em-js-nav-dropdown-trigger.em-is-active')?.classList.remove('em-is-active');
	 
	};
  

	headerLink = (href: string, text: string) => {
		return (
			<Link to={href} className="no-underline"
				onClick={this.closeMenu}
			> 
				<NavigationSubitem label={text} />
			</Link>
		);
	};

	handleSubmittedFeedback = () => {
		this.setState({
			alert: {
				visible: true,
				text: 'Feedback message submitted successfully',
				variant: 'success',
			},
		});
	};

	render() {
		return (
			<>
				<HorizontalHeader
					id="portal-header"
					title={'Cloud Self-Service Portal'}
					withGlobalNav={true}
					optionalClass={
						this.state.expandedDropdown
							? 'em-c-header--large-nav em-u-margin-none em-is-active'
							: 'em-c-header--large-nav em-u-margin-none'
					}
					type="em-c-header--large-nav"
					color="em-c-header--blue"
				>
					<Navigation>
						<Link to="/" className="no-underline">
							<NavigationItem current label="Home" />
						</Link>
						<Link to="/subscriptions" className="no-underline">
							<NavigationItem label="Subscriptions" />
						</Link>
						<Link to="/awsAccounts" className="no-underline">
							<NavigationItem label="AWS Accounts" />
						</Link>
						{authenticatedUser.isInRole(UserRole.OrgAdmins) && (
							<Link to="/organizations" className="no-underline">
								<NavigationItem label="Organizations" />
							</Link>
						)}
						<NavigationItem hasChildren label="Entra ID Services">
							{this.headerLink('/cloudGroups', 'Cloud Groups Management')}
							{this.headerLink('/cloudAccounts', 'Cloud Account Management')}
							{this.headerLink('/adApps', 'Entra ID Applications')}
							{/* {this.headerLink('/ad3rdPartyApps', 'Entra ID 3rd Party Applications')} */}
							{this.headerLink('/tap', 'Temporary Access Password')}
							{this.externalLink('http://goto/b2bbeta', 'Entra ID B2B')}
							{authenticatedUser.isInRole(UserRole.AWSSSORequestor) &&
								this.headerLink('/awsSSO', 'AWS SSO Management')}
							{this.externalLink('https://goto/SSOForm', 'New SAML SSO Consultation')}
							{this.externalLink('http://goto/SSO-Support', 'Existing SAML SSO Consultation')}
						</NavigationItem>
						<NavigationItem hasChildren label="Network">
							{this.headerLink(
								'/portOpeningRequests/connectedAzure',
								'Port Opening Requests: Connected Azure'
							)}
							{this.headerLink(
								'/awsPortOpeningRequest/connectedAWS',
								'Port Opening Request: Connected AWS'
							)}
							{this.headerLink('/onPremPortOpeningRequests/', 'Port Opening Requests: On-Premises')}
							{this.headerLink('/onPremPortOpeningObjects/', 'Object Requests: On-Premises')}
							{this.headerLink('/onPremSiteToSiteVPN/', 'S2S VPN Requests: On-Premises')}
							{this.headerLink(
								'/doNotDecryptRequests',
								'DPI (Deep Packet Inspection) / SSL Exception Requests: Connected Azure'
							)}
							{this.externalLink('https://goto/ddiforms', 'DNS Management')}
						</NavigationItem>
						<NavigationItem hasChildren label="Alibaba Cloud">
							{this.headerLink('/alibabaCloud', 'Ram User Management')}
							{this.headerLink('/alibabaCloudFirewall', 'Cloud Firewall Management')}
							{this.externalLink(
								'https://appwiki.xom.cloud/docs/SpecializedCloud/Alibaba/overview.html',
								' Alibaba Cloud Documentation'
							)}
						</NavigationItem>
						<NavigationItem hasChildren label="Approvals">
							{this.headerLink('/approvals?tab=accounts', 'Cloud Accounts')}
							{this.headerLink('/approvals?tab=apps', 'Entra Id Applications')}
							{this.headerLink('/approvals?tab=pors', 'Port Opening Requests (POR)')}
							{this.headerLink('/approvals?tab=dpis', 'Deep Packet Inspection (DPI)')}
							{this.headerLink('/approvals?tab=taps', 'Temporary Access Password (TAP)')}
							{this.headerLink('/approvals?tab=onpremobjects', 'On-Premises Objects')}
							{this.headerLink('/approvals?tab=s2spors', 'On-Premises S2S VPN')}
							{this.headerLink('/approvals?tab=awspors', 'AWS Port Opening Requests (AWS POR)')}
							{this.headerLink('/approvals?tab=onprempors', 'On-Prem Port Opening Requests')}
							{this.headerLink('/approvals?tab=awsaccounts', 'AWS Accounts')}
						</NavigationItem>
						<NavigationItem hasChildren multiColumn label="Help">
							<>
								<NavigationSubitem
									label={'CONSULTING'}
									optionalClass="em-c-primary-nav__subitem--header"
								/>
								{this.externalLink(Urls.RequestConsultation, 'Request cloud consultation')}
							</>
							<>
								<NavigationSubitem
									label={'DOCUMENTATION'}
									optionalClass="em-c-primary-nav__subitem--header"
								/>
								{this.externalLink(
									'https://appwiki.xom.cloud/docs/admin/cloudssp/CloudSelfServicePortal.html',
									' Cloud SSP Overview'
								)}
								{this.externalLink('https://portal.xom.cloud/swagger', ' Cloud SSP Swagger')}
								{this.externalLink('https://appwiki.xom.cloud/docs/index.html', 'cloudWiki')}
								{this.externalLink('https://docs.ea.xomsvcs.com/', 'EA Public Docs')}
								{this.externalLink('https://docs.microsoft.com/en-us/', 'Microsoft Documentation')}
							</>
							<>
								<NavigationSubitem
									label={'LEARNING'}
									optionalClass="em-c-primary-nav__subitem--header"
								/>
								{this.externalLink(Urls.CloudLearningPaths, 'Cloud Learning Paths')}
								{this.externalLink(
									Urls.SubscriptionOwnerLearningPath,
									'Subscription Owner Learning Path'
								)}
							</>
						</NavigationItem>
						<NavigationItem hasChildren label="Feedback">
							{this.headerLink('/feedback', 'View Feedbacks')}
							<NavigationSubitem
								label={'Submit Feedback'}
								// optionalClass='em-js-modal-trigger'
								onClick={() => {
									this.setState({ createFeedbackModalVisible: true });
								}}
							/>
						</NavigationItem>
					</Navigation>
					<GlobalNav>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
								<GlobalNavItem
									label="Microsoft Azure Portal"
									onClick={() => window.open('https://portal.azure.com/#home', '_blank')}
								/>

								<GlobalNavItem
									label="AppWiki"
									onClick={() => window.open('https://appwiki.xom.cloud/docs/index.html', '_blank')}
								/>
							</div>

							<div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
								<div
									className="em-c-global-nav__item em-u-padding-bottom-none em-u-padding-right"
									style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
								>
									<Switch
										checked={this.state.isDarkTheme}
										onChange={this.handleThemeChange}
										optionalClass="switch-style"
									/>
									&nbsp;<span>Dark Mode</span>
									<Icon name="moon" size="em-c-icon--small" />
								</div>

								<div className="em-u-padding-bottom-none">
									<GlobalNavItem
										hasChildren
										label={authenticatedUser?.displayName}
										avatarSrc={usrImage}
										link="https://designsystem.exxonmobil.com/"
										linkTarget="_blank"
									>
										<GlobalNavSubitem label="Logout" onClick={() => logout()} />
									</GlobalNavItem>
								</div>
							</div>
						</div>
					</GlobalNav>
				</HorizontalHeader>
				<AlertModal
					{...this.state.alert}
					willTimeout
					onClose={() => {
						this.setState({
							alert: {
								visible: false,
							},
						});
					}}
				/>
				{this.state.createFeedbackModalVisible && (
					<CreateFeedbackModal
						visible
						onConfirm={() => {
						this.setState({ createFeedbackModalVisible: false }, () => {
							this.handleSubmittedFeedback();
						});
						}}
						onClose={() => {
						this.setState({ createFeedbackModalVisible: false });
						}}
					/>
				)}
			</>
		);
	}
}
